import { useContext } from 'react'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import useTaskListTemplateItem, { useTaskListTemplateItemForm } from '@hooks/useTaskListTemplateItem'
import useTemplateQualityControlCategories from '@hooks/useTemplateQualityControlCategories'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

const MODAL_KEY = 'CreateOrEditTaskListTemplateItemModal'

type ModalCallbacks = {
  closeModal: () => void,
  createTaskListTemplateItem: (payload: HandleCallbackActionParams) => void,
  updateTaskListTemplateItem: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditTaskListTemplateItemModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, createTaskListTemplateItem, updateTaskListTemplateItem } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = modalData?.[MODAL_KEY] ?? {}
  const taskListTemplateItem = modalPayload?.taskListTemplateItem ?? {}

  const newRecord = !taskListTemplateItem.id

  const {
    callbacks: { createTaskListTemplateItem: createFn, updateTaskListTemplateItem: updateFn },
    creating,
    updating,
  } = useTaskListTemplateItem(taskListTemplateItem)

  const {
    entityState,
    entityState: {
      assigned_points, description, qc_category_key, title,
    },
    errors,
    handlers,
    saveEnabled,
  } = useTaskListTemplateItemForm(taskListTemplateItem, { validateOn: 'change' })

  // To be used with handleCallbackAction
  const createOrUpdateTaskListTemplateItemPayload = {
    callbacks: {
      action: newRecord ? createFn : updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `Task List Template Item ${newRecord ? 'Created' : 'Updated'}`,
  }

  const qualityControlCategoriesPayload = useTemplateQualityControlCategories({ performHttpRequests: true })
  const { templateQualityControlCategories } = qualityControlCategoriesPayload

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        title={newRecord ? 'Create' : 'Edit'}
        titleSecondLine="Checklist Item"
      />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="Category">
            {/*
             // @ts-expect-error TS2322 */}
            <select name="qc_category_key" value={qc_category_key} {...handlers}>
              <option value="">Please Select...</option>
              {templateQualityControlCategories
                && templateQualityControlCategories.map((category: { title: string, key: string, id: string }) => (
                  <option key={category.key} value={category.key}>
                    {category.title}
                  </option>
                ))}
            </select>
          </Form.Field>

          <Form.Field label="* Title" errorMessage={errors.title} marginTop="large">
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>

          <Form.Field label="Description" errorMessage={errors.description} marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <textarea name="description" style={{ height: 100 }} value={description} {...handlers} />
          </Form.Field>

          <Form.Field label="* Assigned Points" errorMessage={errors.assigned_points} marginTop="large">
            <input name="assigned_points" step="1" type="number" value={assigned_points} {...handlers} />
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          onClick={
            newRecord
              ? () => createTaskListTemplateItem(createOrUpdateTaskListTemplateItemPayload)
              : () => updateTaskListTemplateItem(createOrUpdateTaskListTemplateItemPayload)
          }
          size="large"
        >
          {saveEnabled ? `${newRecord ? 'Create' : 'Update'} Checklist Item` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditTaskListTemplateItemModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
